/*
================================================================================
                    WI GRID PAGE - BUSINESS LOGIC
================================================================================
PURPOSE: WI slip management dashboard with 24-hour validity tracking

KEY FEATURES:
• Real-time WI slip monitoring with 24-hour validity window
• Slip lifecycle management: Created → Active → Completed/Cancelled
• PDF generation with reprint tracking and date stamping
• Welder avatar display with color-coded identification
• Bulk operations: Cancel, Delete, Export with status validation

DATA STRUCTURE:
• 24-hour GSI index for active slip retrieval
• Status filtering: Created, Cancelled, Deleted, Completed
• Print tracking: Original print date + reprint counter
• Welder continuity: Last weld date updates on slip usage

BUSINESS RULES:
• 24-hour slip validity from creation timestamp
• Completed slips cannot be modified or cancelled
• Cancelled slips can only be deleted or exported
• PDF generation updates print counter and reprint date
• Slip deletion removes from active tracking but maintains audit trail

INTEGRATION POINTS:
• WI API: Slip CRUD operations and status management
• WPS API: Procedure details for PDF generation
• Process API: Category mapping for slip display
• Export API: Bulk data extraction for reporting

================================================================================
*/

import  { useState, useRef, forwardRef, useImperativeHandle, useEffect } from "react";
import GolbalSearch from "../utility/common/helpers/GolbalSearch";
import { handleExport1 } from "../utility/data/export/GlobalExport";
import { Row, Col, Container } from "react-bootstrap";
import GlobalColumnDefinations, { getHeader_FieldName } from "../utility/grid/definitions/AgGridDefination";
import Toaster, { showSuccess, showWarning } from "../utility/ui/notifications/Toaster";
import GridMaster from "../utility/grid/GridMaster";
import AddEditWIPage from "./AddEditWIPage";
import { CancelSlip, DeleteSlip, getallWelderid, getallwelders, UpdateSlip, getWIBySlipNumber } from "../../Services/WI-services";
import WiPdfDownload from "./PDF/WiPdfDownload";
import UnifiedActionRenderer from "../utility/grid/renderers/ActionRenderers/UnifiedActionRenderer";
import { getdetailsById } from "../../Services/wps_details-service";
import { GridLoader } from "react-spinners";
import Avatar from "react-avatar";
import { getWeldingProcessByProcess } from "../../Services/weldingprocess-services";
import { BiX } from "../../icons/bi";
import { ICONS } from "../utility/common/constants/icons";

const AvatarRenderer = (params) => {
  const colors = ["avatarcolor1", "avatarcolor2", "avatarcolor3"];
  const name = params.data.WI_Welder_Name;
  const index = params.rowIndex;
  const colorIndex = index % colors.length;
  const selectedColor = colors[colorIndex];

  return (
    <>
      <Avatar
        className={selectedColor}
        maxInitials={2}
        name={name}
        round={true}
        marginTop="120"
        size="35"
      />
      &nbsp;&nbsp;
      <span>{name}</span>
    </>
  );
};

/*
 * WIGridPage Component - WI slip dashboard and management
 * 
 * COMPONENT WORKFLOW:
 * 1. Load active slips using 24-hour GSI index
 * 2. Display welder avatars with color-coded identification
 * 3. Handle slip actions: Cancel, Delete, PDF generation
 * 4. Track print history and reprint dates
 * 5. Manage slip lifecycle and status transitions
 */
const WIGridPage = forwardRef((props, ref) => {
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [filteredResult, setFilteredResult] = useState([]);
  const [columnDefs, setcolumnDefs] = useState([]);
  const [widata, setwidata] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [wpsde, setwpsde] = useState({});
  const [showFormadd, setShowFormadd] = useState(false);
  const [cancel, setCancel] = useState(false);
  const [action, setAction] = useState("");
  const [reload, setReload] = useState(false);
  const [, setExcelData] = useState([]);
  const [slipNumber, setSlipNumber] = useState("");
  const [previousSlipNumber, setPreviousSlipNumber] = useState("");
  const [isSlipSearch, setIsSlipSearch] = useState(false);
  const childRef = useRef();
  const tmpcolumnDefs = [];

  const handleCancelEvent = async (row) => {
    try {
      setRowData(row);
      row.WI_Slip_Status = "Cancelled";
      await CancelSlip(row);
      showSuccess(`Selected Slip is Cancelled`);
      if (cancel === false) {
        setCancel(true);
      } else {
        setCancel(false);
      }
    } catch (error) {
      console.error("Cancellation failed:", error);
    }
  };

  const handlepdf = async (row) => {
    const resultWI = await getallWelderid(row?.SK);
    let wiProcess = resultWI[0].WI_Process;
    const resultWPS = await getdetailsById(row?.WI_Project_No, resultWI[0]?.WI_WPS_No);

    let wpsDetailData = resultWPS.filter((item) => item.WPSD_Process === wiProcess);
    let procCat = await getWeldingProcessByProcess({ "WPSD_Process": wpsDetailData[0]?.WPSD_Process });
    if (procCat) {
      wpsDetailData[0]["WPSDProcessCat"] = procCat[0]?.wld_p_category;
    }
    if (wpsDetailData[0].wld_WPSType === "Tubesheet") {
      wpsDetailData[0]["WPSDType"] = wpsDetailData[0]?.WPSD_WP_Type;
    }
    else {
      wpsDetailData[0]["WPSDType"] = wpsDetailData[0]?.WPSD_Type;
    }
    setwpsde(wpsDetailData[0]);
    const combinedData = [resultWI[0], ...wpsDetailData];
    setRowData(combinedData);
    UpdateSlip(row);
    setAction("pdf");
    setShowForm(true);
  };

  const headerName = (field) => {
    let name = getHeader_FieldName(field, tmpcolumnDefs, "getHeader");
    return name;
  };

  const fieldName = (header) => {
    let name = getHeader_FieldName(header, tmpcolumnDefs, "getField");
    return name;
  };

  const fetchwidata = async () => {
    setLoading(true);
    setTimeout(async () => {
      const result = await getallwelders();
      setwidata(result);
      setFilteredResult(result);
      setLoading(false);
    }, 2000); // Delay of 2 seconds
  };

  const handleSlipNumberBlur = async () => {
    const value = slipNumber.trim();

    // Only fetch data if the value has actually changed
    if (value !== previousSlipNumber) {
      setPreviousSlipNumber(value);

      if (value) {
        setIsSlipSearch(true);
        await fetchSlipData(value);
      } else {
        setIsSlipSearch(false);
        await fetchwidata(); // Reload 24hr data
      }
    }
  };

  const handleClearSlipNumber = async () => {
    setSlipNumber("");
    setPreviousSlipNumber("");
    setIsSlipSearch(false);
    await fetchwidata(); // Reload 24hr data
  };


  const fetchSlipData = async (slipNo) => {
    setLoading(true);
    try {
      const result = await getWIBySlipNumber(slipNo);
      if (result?.length > 0) {
        setwidata(result);
        setFilteredResult(result);
        console.log(`Fetched slip data: ${result.length} records`);
      } else {
        showWarning("Invalid slip number");
        setwidata([]);
        setFilteredResult([]);
      }
    } catch (error) {
      console.error("Error fetching slip data:", error);
      setwidata([]);
      setFilteredResult([]);
    } finally {
      setLoading(false);
    }
  };


  const fetchColumnDefns = async (params) => {
    const result = GlobalColumnDefinations(tmpcolumnDefs);
    result.push({
      headerName: "Project Number",
      field: "WI_Project_No",
      type: "Text",
      isCheckbox: false,
    });

    result.push({
      headerName: "Joint Number",
      field: "WI_Joint_No",
      type: "Text",
      isCheckbox: false,
    });
    result.push({
      headerName: "Welder Code",
      field: "WI_Welder_Id",
      type: "Text",
      isCheckbox: false
    });
    result.push({
      headerName: "Welder Name",
      field: "WI_Welder_Name",
      type: "Text",
      isCheckbox: false,
      cellRenderer: AvatarRenderer
    });
    result.push({
      headerName: "",
      cellRenderer: actionRenderer,
      Width: 250,
      suppressMenu: true,
      sortable: false
    });
    setcolumnDefs(result);
  };

  useEffect(() => {
    fetchwidata();
    fetchColumnDefns();
  }, [reload, cancel]);

  useImperativeHandle(ref, () => ({
    handleAdd: async () => {
      setAction("Add");
      setShowFormadd(true);
    },
  }));

  const resetForm = () => {
    document.filterForm.reset();
    setFilteredResult(widata);
  };

  const handleGlobalSearch = (text) => {
    childRef.current.setSearch(text);
  };

  const handleGlobalExport = () => {
    let filename = "WI";
    handleExport1(filteredResult, filename, columnDefs);
  };

  const actionRenderer = (params) => {
    return (
      <>
        {params.data.WI_24hrs === "Completed" ? (
          ""
        ) : (
          <UnifiedActionRenderer
            selectedData={params}
            actions={params.data.WI_Slip_Status === "Cancelled" ? ['delete', 'export'] : ['cancel', 'delete', 'export']}
            handleCancelEvent={(params) => handleCancelEvent(params.data)}
            handleDeleteEvent={(params) => handleDeleteEvent(params.data)}
            handlePdfEvent={(params) => handlepdf(params.data)}
          />
        )}
      </>
    );
  };

  const handleDeleteEvent = async (row) => {
    try {
      setRowData(row);
      row.WI_Slip_Status = "Deleted";
      await DeleteSlip(row);
      showSuccess(`Selected Slip is Deleted`);
      if (cancel === false) {
        setCancel(true);
      } else {
        setCancel(false);
      }
    } catch (error) {
      console.error("Deletion failed:", error);
    }
  };

  return (
    <>
      {loading && (
        <div className="loader-overlay">
          <GridLoader size={10} className="pageloader" />
        </div>
      )}
      <Container fluid>

        <Row className="RowHeight">
          <Col md={2} sm={12} xs={12} className="ListTitle">
            WI list
          </Col>
          <Col md={2} sm={12} xs={12}>
            <div style={{ display: "flex", alignItems: "center", position: "relative", width: "100%" }}>
              <input
                type="text"
                id="slipNumber"
                name="slipNumber"
                placeholder="Slip Number"
                value={slipNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, '');
                  setSlipNumber(value);
                }}
                onBlur={handleSlipNumberBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSlipNumberBlur();
                  }
                }}
                style={{
                  width: "100%",
                  padding: "8px",
                  border: "1px solid var(--col-ddd)",
                  borderRadius: "4px",
                  fontSize: "12px",
                  // outline: "none",
                }}
              />
              <BiX
                size={20}
                onClick={handleClearSlipNumber}
                style={{
                  position: "absolute",
                  right: "4px",
                  cursor: "pointer",
                  color: "#707070"
                }}
              />
            </div>
          </Col>
          <Col>
            <div className="global-search-container">

              <GolbalSearch
                filterFunc={handleGlobalSearch}
                searchplaceholdertext={
                  "Search for Project Number, Joint Number, Welder Code, Welder Name"
                }
              />
              <img className="allexport"
                src={ICONS.Export}
                onMouseOver={(e) => (e.currentTarget.src = ICONS.ExportBlue)}
                onMouseOut={(e) => (e.currentTarget.src = ICONS.Export)}
                alt="ExportIcon"
                onClick={handleGlobalExport}
              />
            </div>
          </Col>
        </Row>
        <GridMaster
          rowDetails={filteredResult}
          colDetails={columnDefs}
          fieldNames={fieldName}
          headerNames={headerName}
          setExcelData={setExcelData}
          ref={childRef}
        />
        {showForm && (
          <WiPdfDownload
            show={showForm}
            action={action}
            rowdata={rowData}
            wpsde={wpsde}
            onHide={() => setShowForm(false)}
            setReload={setReload}
            reload={reload}
            widata={widata}
            backdrop="static"
          />
        )}
        {showFormadd && (
          <AddEditWIPage
            show={showFormadd}
            action={action}
            rowdata={rowData}
            onHide={() => setShowFormadd(false)}
            resetForm={resetForm}
            setReload={setReload}
            reload={reload}
            // fetchUserMaster={fetchWeldingProcess}
            allRecs={widata}
            backdrop="static"
          />
        )}
        <Toaster />
      </Container>
    </>
  );
});

export default WIGridPage;
